/**
 * One market: what NEAR Intents has settled for this chain's token.
 *
 * Three readings, top to bottom, in order of how settled they are. The price
 * figures and the listing evidence first — the same panel the network page
 * carries — then swaps as they settle, then the history they join.
 *
 * Empty is a state here, not an error. Until the token is bridged, the honest
 * page is the evidence that nothing can have traded, and that is what the
 * header shows; the feed and the history then say plainly that there is
 * nothing in them yet. blackbeard/observer#32.
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import type { IntentsSwap } from '../api/generated/IntentsSwap'
import type { IntentsSwaps } from '../api/generated/IntentsSwaps'
import { RequestFailed, fetchMarkets, fetchSwapsNewest } from '../api/rest'
import { height as fmtHeight } from '../lib/format'
import { useObserverInstance } from '../lib/observer-context'
import { href } from '../lib/routes'
import { BridgeLinksPanel } from './BridgeLinksPanel'
import { BridgePanel } from './BridgePanel'
import { IntentsFlowPanel } from './IntentsFlowPanel'
import { FillsPanel } from './FillsPanel'
import { IntentsPanel } from './IntentsPanel'
import { InventoryPanel } from './InventoryPanel'
import { PriceChart } from './PriceChart'
import { QuotePanel } from './QuotePanel'
import { PoolPriceChart } from './RefDclMarket'
import { RefDclMarket } from './RefDclMarket'
import { SwapHead, SwapRow } from './SwapRow'

/** The venues this page knows how to show. */
const KNOWN = new Set(['near-intents', 'ref-dcl'])

/** Lines the live feed keeps. Older ones are in the history below it. */
const LIVE_LINES = 12

export function MarketPage({
  chain,
  market,
  decimals,
  symbol,
}: {
  chain: string
  market: string
  decimals: number
  symbol: string
}) {
  if (!KNOWN.has(market)) {
    return (
      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <h2 className="panel-title">No such market</h2>
        </div>
        <p className="empty">
          This chain&apos;s token is not followed on a market called “{market}”.{' '}
          <Link to={href({ chain, index: 'market' })}>The markets it is followed on</Link>.
        </p>
      </section>
    )
  }
  if (market === 'ref-dcl') {
    return <RefDclMarket chain={chain} decimals={decimals} symbol={symbol} />
  }
  return <NearIntents chain={chain} decimals={decimals} symbol={symbol} />
}

function NearIntents({
  chain,
  decimals,
  symbol,
}: {
  chain: string
  decimals: number
  symbol: string
}) {
  const [history, setHistory] = useState<IntentsSwaps | null>(null)
  const [older, setOlder] = useState<IntentsSwap[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [live, setLive] = useState<{ key: number; swap: IntentsSwap }[]>([])
  // A monotonic key rather than height-and-index: the wormhole console's keys
  // looked unique and were not, and React given duplicate keys renders
  // duplicates and stops honouring the list's own length cap.
  const nextKey = useRef(0)
  const observer = useObserverInstance()
  // `null` until the market list answers. The pool chart sits at the top of
  // the page, so it is drawn while that is unknown and removed only if there
  // turns out to be no Ref market: waiting for `true` first would drop it in
  // above everything a moment after load and shove the page down.
  const [refMarket, setRefMarket] = useState<boolean | null>(null)
  useEffect(() => {
    const controller = new AbortController()
    fetchMarkets(chain, controller.signal)
      .then((m) => setRefMarket(m.markets.some((x) => x.id === 'ref-dcl')))
      .catch(() => {})
    return () => controller.abort()
  }, [chain])

  useEffect(() => {
    const controller = new AbortController()
    setHistory(null)
    setOlder([])
    setError(null)
    setLive([])
    fetchSwapsNewest(chain, null, controller.signal)
      .then(setHistory)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain])

  useEffect(
    () =>
      observer.onIntentsSwap((swap) => {
        const key = nextKey.current++
        setLive((rows) => [{ key, swap }, ...rows].slice(0, LIVE_LINES))
      }),
    [observer],
  )

  const asset = history?.asset_symbol ?? symbol
  const assetDecimals = history?.asset_decimals ?? decimals
  // The previous page's `next`, carried forward as each older page is read.
  const cursor = history?.next ?? null

  const loadOlder = () => {
    if (!cursor) return
    setLoadingOlder(true)
    fetchSwapsNewest(chain, cursor)
      .then((page) => {
        setOlder((rows) => [...rows, ...page.swaps])
        setHistory((h) => (h ? { ...h, next: page.next } : page))
      })
      .catch((e: unknown) =>
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.'),
      )
      .finally(() => setLoadingOlder(false))
  }

  const rows = history ? [...history.swaps, ...older] : []
  const range =
    history && history.indexed_from !== null && history.indexed_to !== null
      ? `NEAR blocks ${fmtHeight(history.indexed_from)}–${fmtHeight(history.indexed_to)}`
      : 'nothing read yet'

  return (
    <>
      <p className="panel-note" style={{ marginBottom: 12 }}>
        <Link to={href({ chain, index: 'market' })}>Markets</Link> › NEAR Intents
      </p>

      {/* First, straight under the breadcrumb: 1Click fills carry no price, so
          the price on this page is the one the solver pays to restock — the
          Ref pool's swaps (#43) — and it is what a reader comes here for. */}
      {refMarket !== false && <PoolPriceChart chain={chain} symbol={symbol} />}

      {refMarket && (
        <p className="panel-note" style={{ marginBottom: 12 }}>
          <strong>{symbol}&apos;s liquidity on NEAR is also in a Ref DCL pool.</strong> Solvers
          restock from it after filling users here; those swaps are on the{' '}
          <Link to={href({ chain, market: 'ref-dcl' })}>Ref Finance DCL market</Link>, and are not
          counted on this one, so the same demand is never counted twice.
        </p>
      )}

      <IntentsPanel chain={chain} decimals={decimals} symbol={symbol} tiles={8} table />

      <QuotePanel chain={chain} symbol={symbol} />

      <BridgePanel chain={chain} decimals={decimals} symbol={symbol} />

      <BridgeLinksPanel chain={chain} symbol={symbol} />

      <IntentsFlowPanel chain={chain} symbol={symbol} />

      <FillsPanel chain={chain} symbol={symbol} />

      <PriceChart chain={chain} symbol={symbol} />

      <InventoryPanel chain={chain} symbol={symbol} />

      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <h2 className="panel-title">Settling now</h2>
          <span className="eyebrow">pushed as each block is read</span>
        </div>
        {live.length === 0 ? (
          <p className="empty">
            No {asset} trade has settled as a token swap. 1Click settles {asset} as transfers
            instead, listed under &ldquo;1Click fills&rdquo; above.
          </p>
        ) : (
          <div className="scroll-x">
            <table className="board">
              <caption className="visually-hidden">Swaps as they settle, newest first</caption>
              <SwapHead asset={asset} />
              <tbody>
                {live.map(({ key, swap }) => (
                  <SwapRow
                    chain={chain}
                    key={key}
                    swap={swap}
                    asset={asset}
                    decimals={assetDecimals}
                    fresh
                    relative
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <h2 className="panel-title">Settled</h2>
          <span className="eyebrow">{range}</span>
        </div>
        {error && <p className="empty">{error}</p>}
        {!error && !history && <p className="empty">Reading…</p>}
        {history && rows.length === 0 && (
          <p className="empty">
            No {asset} trade has settled as a token swap in the blocks read ({range}). 1Click
            settles {asset} as transfers instead; those are the fills above.
          </p>
        )}
        {rows.length > 0 && (
          <div className="scroll-x">
            <table className="board">
              <caption className="visually-hidden">Settled swaps, newest first</caption>
              <SwapHead asset={asset} />
              <tbody>
                {rows.map((swap) => (
                  <SwapRow
                    chain={chain}
                    key={`${swap.near_height}:${swap.trade_index}`}
                    swap={swap}
                    asset={asset}
                    decimals={assetDecimals}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cursor && (
          <div className="pager">
            <button className="segmented panel-button" disabled={loadingOlder} onClick={loadOlder}>
              {loadingOlder ? 'Reading…' : 'Older →'}
            </button>
          </div>
        )}
        <p className="panel-note">
          A row is one user&apos;s side of a swap, priced from what they gave and what they got.
          “Between solvers” is market makers trading with each other: a real trade at a real price,
          with no taker and so no side. “Via” is the front end the taker came through, as it named
          itself. A solver&apos;s routing through other tokens is how a swap was filled, never a
          trade of its own, and is not listed.
        </p>
      </section>
    </>
  )
}
